import type {
  PortalComment,
  PortalForm,
  PortalRequest,
  PortalTaskDetail,
  PortalTaskSummary,
  PresignedUpload,
  SubmitResult,
} from '@pm/schemas';
import {
  Badge,
  type BadgeTone,
  Button,
  Checkbox,
  cn,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LabelCaps,
  Markdown,
  MarkdownEditor,
  Select,
  Skeleton,
  SkeletonRows,
  StatusBadge,
  Tabs,
  Textarea,
} from '@pm/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronRight,
  Download,
  FilePlus2,
  Inbox,
  ListTodo,
  Paperclip,
  Send,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { fmtDateTime, fmtDue, fmtRelative, str } from '@/lib/format';
import { REQUEST_STATUS_LABEL } from '@/features/forms/form-bits';
import { useToast } from '@/lib/toast';
import { portalApi } from './portal-api';

const keys = {
  tasks: (scope: string) => ['portal', 'tasks', scope] as const,
  task: (id: string) => ['portal', 'task', id] as const,
  forms: ['portal', 'forms'] as const,
  requests: ['portal', 'requests'] as const,
};

// ---------- Demandas ----------

export function PortalTasksPage() {
  const [scope, setScope] = useState<'open' | 'approval' | 'done'>('open');
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: keys.tasks(scope),
    queryFn: () => portalApi.get<PortalTaskSummary[]>(`/tasks?scope=${scope}`),
  });
  const { data: approvals } = useQuery({
    queryKey: keys.tasks('approval'),
    queryFn: () => portalApi.get<PortalTaskSummary[]>('/tasks?scope=approval'),
    staleTime: 30_000,
  });
  const rows = data ?? [];
  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-xl font-medium">Suas demandas</h1>
          <p className="text-sm text-text-2">
            Acompanhe o andamento do que a equipe está fazendo para você.
          </p>
        </div>
        <Tabs
          value={scope}
          onChange={setScope}
          tabs={[
            { value: 'open', label: 'Em andamento' },
            { value: 'approval', label: 'Aguardando você', count: approvals?.length || undefined },
            { value: 'done', label: 'Concluídas' },
          ]}
        />
      </header>
      {isError ? <ErrorState message={error.message} onRetry={() => void refetch()} /> : null}
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={scope === 'approval' ? ShieldCheck : ListTodo}
            title={scope === 'approval' ? 'Nada para aprovar' : 'Nenhuma demanda por aqui'}
            description={
              scope === 'open'
                ? 'Quando a equipe compartilhar uma demanda com você, ela aparece aqui.'
                : ''
            }
          />
        ) : (
          <ul>
            {rows.map((t) => (
              <TaskRow key={t.id} t={t} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TaskRow({ t }: { t: PortalTaskSummary }) {
  return (
    <li>
      <Link
        to={`/portal/demandas/${t.id}`}
        className="flex items-center gap-3 border-b border-border px-4 py-3 hover:bg-surface-2 last:border-b-0"
      >
        <span className="w-12 shrink-0 font-mono text-xs text-text-3">#{t.number}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{t.title}</p>
          <p className="flex flex-wrap gap-x-3 font-mono text-xs text-text-3">
            {t.stage ? <span>etapa: {t.stage}</span> : null}
            {t.responsible ? <span>com {t.responsible.name}</span> : null}
            {t.subtasks_total > 0 ? (
              <span>
                {t.subtasks_completed}/{t.subtasks_total} etapas internas
              </span>
            ) : null}
          </p>
        </div>
        {t.awaiting_client_approval ? (
          <Badge tone="accent">aguardando você</Badge>
        ) : (
          <StatusBadge status={t.status} />
        )}
        <span
          className={cn(
            'hidden font-mono text-xs sm:inline',
            t.is_overdue ? 'text-danger' : 'text-text-3',
          )}
        >
          {fmtDue(t.due_at)}
        </span>
        <ChevronRight className="size-4 text-text-3" />
      </Link>
    </li>
  );
}

const HISTORY_LABEL: Record<string, string> = {
  'task.created': 'demanda criada',
  'task.started': 'trabalho iniciado',
  'task.completed': 'concluída',
  'task.canceled': 'cancelada',
  'task.approval_requested': 'enviada para sua aprovação',
  'task.approved': 'aprovada',
  'task.rejected': 'devolvida para ajustes',
  'task.stage_changed': 'avançou de etapa',
  'task.due_changed': 'prazo alterado',
  'attachment.added': 'anexo adicionado',
};

export function PortalTaskPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const {
    data: t,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: keys.task(id),
    queryFn: () => portalApi.get<PortalTaskDetail>(`/tasks/${id}`),
  });
  const [comment, setComment] = useState('');
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);
  const [note, setNote] = useState('');
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: keys.task(id) });
    void qc.invalidateQueries({ queryKey: ['portal', 'tasks'] });
  };
  const send = useMutation({
    mutationFn: (input: { content_markdown: string; attachment_ids: string[] }) =>
      portalApi.post<PortalComment>(`/tasks/${id}/comments`, input),
    onSuccess: invalidate,
  });
  const decide = useMutation({
    mutationFn: (v: { action: 'approve' | 'reject'; comment?: string }) =>
      portalApi.post<PortalTaskDetail>(`/tasks/${id}/${v.action}`, { comment: v.comment }),
    onSuccess: invalidate,
  });
  const upload = useUploader(id, invalidate);

  if (isLoading)
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="w-40" />
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-40 rounded-md" />
      </div>
    );
  if (isError || !t)
    return (
      <ErrorState
        message={error?.message ?? 'Demanda não encontrada.'}
        onRetry={() => void refetch()}
      />
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-1.5 font-mono text-xs text-text-3">
        <Link to="/portal" className="hover:text-text-1">
          Demandas
        </Link>
        <ChevronRight className="size-3" />
        <span>#{t.number}</span>
      </div>
      <header className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-medium">{t.title}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-text-2">
            <StatusBadge status={t.status} />
            {t.stage ? (
              <span className="font-mono text-xs text-text-3">etapa: {t.stage}</span>
            ) : null}
            {t.responsible ? (
              <span className="font-mono text-xs text-text-3">com {t.responsible.name}</span>
            ) : null}
            <span className={cn('font-mono text-xs', t.is_overdue ? 'text-danger' : 'text-text-3')}>
              {fmtDue(t.due_at)}
            </span>
          </p>
        </div>
      </header>

      {t.can_approve ? (
        <section className="flex flex-col gap-3 rounded-md border border-accent-border bg-accent-soft p-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-4 text-accent" />
            <span className="font-medium">A equipe pediu sua aprovação</span>
          </div>
          {t.approval_comment ? <p className="text-sm text-text-2">{t.approval_comment}</p> : null}
          {decision ? (
            <div className="flex flex-col gap-2">
              <Textarea
                autoFocus
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  decision === 'approve' ? 'Comentário (opcional)' : 'O que precisa ser ajustado?'
                }
                className="min-h-[72px] bg-surface-1"
              />
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setDecision(null)}>
                  Cancelar
                </Button>
                <Button
                  variant={decision === 'approve' ? 'primary' : 'danger'}
                  size="sm"
                  loading={decide.isPending}
                  disabled={decision === 'reject' && !note.trim()}
                  onClick={() =>
                    decide.mutate(
                      { action: decision, comment: note.trim() || undefined },
                      {
                        onSuccess: () => (
                          toast.success(
                            decision === 'approve'
                              ? 'Aprovado! A equipe foi avisada.'
                              : 'Devolvido para ajustes.',
                          ),
                          setDecision(null),
                          setNote('')
                        ),
                        onError: (e) => toast.error(e),
                      },
                    )
                  }
                >
                  {decision === 'approve' ? (
                    <>
                      <Check /> Confirmar aprovação
                    </>
                  ) : (
                    <>
                      <X /> Pedir ajustes
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="primary" size="sm" onClick={() => setDecision('approve')}>
                <Check /> Aprovar
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setDecision('reject')}>
                <X /> Pedir ajustes
              </Button>
            </div>
          )}
        </section>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
        <div className="flex flex-col gap-4">
          {t.description ? (
            <section className="rounded-md border border-border bg-surface-1 p-4">
              <LabelCaps>Descrição</LabelCaps>
              <div className="mt-2 text-sm">
                <Markdown>{t.description}</Markdown>
              </div>
            </section>
          ) : null}
          {t.subtasks.length > 0 ? (
            <section className="rounded-md border border-border bg-surface-1">
              <header className="border-b border-border px-4 py-2.5">
                <LabelCaps>Etapas do trabalho</LabelCaps>
              </header>
              <ul>
                {t.subtasks.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center gap-3 border-b border-border px-4 py-2 text-sm last:border-b-0"
                  >
                    <span
                      className={cn(
                        'grid size-4 place-items-center rounded-full border text-[10px]',
                        s.status === 'COMPLETED'
                          ? 'border-success bg-success text-text-inverse'
                          : 'border-border-strong text-text-3',
                      )}
                    >
                      {s.status === 'COMPLETED' ? (
                        <Check className="size-2.5" strokeWidth={3} />
                      ) : (
                        ''
                      )}
                    </span>
                    <span
                      className={cn(
                        'flex-1',
                        s.status === 'COMPLETED' && 'text-text-3 line-through',
                      )}
                    >
                      {s.title}
                    </span>
                    <span className="font-mono text-xs text-text-3">{fmtDue(s.due_at)}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <section className="flex flex-col gap-3 rounded-md border border-border bg-surface-1 p-4">
            <LabelCaps>Conversa com a equipe</LabelCaps>
            {t.comments.length === 0 ? (
              <p className="text-sm text-text-3">Nenhuma mensagem ainda.</p>
            ) : (
              <ul className="flex flex-col gap-3">
                {t.comments.map((c) => (
                  <li
                    key={c.id}
                    className={cn(
                      'rounded-md border px-3 py-2',
                      c.author.kind === 'CLIENT'
                        ? 'border-accent-border bg-accent-soft'
                        : 'border-border bg-bg',
                    )}
                  >
                    <p className="flex items-center gap-2 text-xs">
                      <span className="font-medium">{c.author.name}</span>
                      <Badge tone={c.author.kind === 'CLIENT' ? 'accent' : 'neutral'}>
                        {c.author.kind === 'CLIENT' ? 'você' : 'equipe'}
                      </Badge>
                      <span className="ml-auto font-mono text-text-3">
                        {fmtRelative(c.created_at)}
                      </span>
                    </p>
                    <div className="mt-1 text-sm">
                      <Markdown>{c.content_markdown}</Markdown>
                    </div>
                    {c.attachments.length ? (
                      <ul className="mt-1 flex flex-wrap gap-2">
                        {c.attachments.map((a) => (
                          <AttachmentChip key={a.id} id={a.id} filename={a.filename} />
                        ))}
                      </ul>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {t.can_comment ? (
              <form
                className="flex flex-col gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!comment.trim()) return;
                  send.mutate(
                    { content_markdown: comment.trim(), attachment_ids: upload.readyIds },
                    {
                      onSuccess: () => (setComment(''), upload.reset()),
                      onError: (err) => toast.error(err),
                    },
                  );
                }}
              >
                <MarkdownEditor
                  value={comment}
                  onChange={setComment}
                  placeholder="Escreva para a equipe…"
                  minRows={3}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={upload.pick}
                    loading={upload.busy}
                  >
                    <Paperclip /> Anexar
                  </Button>
                  {upload.files.map((f) => (
                    <span key={f.id} className="font-mono text-xs text-text-2">
                      {f.filename}
                    </span>
                  ))}
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    className="ml-auto"
                    loading={send.isPending}
                    disabled={!comment.trim()}
                  >
                    <Send /> Enviar
                  </Button>
                </div>
              </form>
            ) : null}
          </section>
        </div>
        <aside className="flex flex-col gap-4 self-start">
          <section className="flex flex-col gap-2 rounded-md border border-border bg-surface-1 p-4">
            <div className="flex items-center gap-2">
              <LabelCaps>Arquivos</LabelCaps>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-6 text-xs"
                onClick={upload.pickForTask}
                loading={upload.busy}
              >
                <Paperclip /> Enviar
              </Button>
            </div>
            {t.attachments.length === 0 ? (
              <p className="text-xs text-text-3">Nenhum arquivo.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {t.attachments.map((a) => (
                  <li key={a.id} className="flex items-center gap-2 text-sm">
                    <AttachmentChip id={a.id} filename={a.filename} />
                    <span className="ml-auto font-mono text-[10px] text-text-3">
                      {a.uploaded_by?.kind === 'CLIENT' ? 'você' : 'equipe'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="flex flex-col gap-2 rounded-md border border-border bg-surface-1 p-4">
            <LabelCaps>Histórico</LabelCaps>
            <ul className="flex flex-col gap-1.5">
              {t.history.map((h) => (
                <li key={h.id} className="text-xs">
                  <span className="text-text-2">
                    {h.actor
                      ? `${h.actor.name}${h.actor.kind === 'CLIENT' ? ' (você)' : ''}`
                      : 'Sistema'}
                  </span>{' '}
                  <span className="text-text-3">{HISTORY_LABEL[h.action] ?? h.action}</span>
                  {h.payload?.comment ? (
                    <span className="text-text-3">: “{str(h.payload.comment)}”</span>
                  ) : null}
                  <div className="font-mono text-[10px] text-text-3">
                    {fmtDateTime(h.created_at)}
                  </div>
                </li>
              ))}
              {t.history.length === 0 ? (
                <li className="text-xs text-text-3">Sem eventos.</li>
              ) : null}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}

function AttachmentChip({ id, filename }: { id: string; filename: string }) {
  const toast = useToast();
  return (
    <button
      type="button"
      className="inline-flex max-w-full items-center gap-1 rounded border border-border px-1.5 py-0.5 font-mono text-xs text-text-2 hover:text-text-1"
      onClick={() =>
        void portalApi.get<{ url: string }>(`/attachments/${id}/download`).then(
          (r) => window.open(r.url, '_blank', 'noopener'),
          (e: unknown) => toast.error(e),
        )
      }
    >
      <Download className="size-3" />
      <span className="truncate">{filename}</span>
    </button>
  );
}

/** Upload via URL assinada: presign → PUT no storage → confirmar. */
function useUploader(taskId: string, onDone: () => void) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<{ id: string; filename: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const modeRef = useRef<'comment' | 'task'>('comment');
  const run = async (file: File) => {
    setBusy(true);
    try {
      const p = await portalApi.post<PresignedUpload>(`/tasks/${taskId}/attachments/presign`, {
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        size: file.size,
      });
      const put = await fetch(p.upload_url, { method: 'PUT', headers: p.headers, body: file });
      if (!put.ok) throw new Error('Falha ao enviar o arquivo.');
      await portalApi.post(`/attachments/${p.attachment.id}/complete`);
      if (modeRef.current === 'comment')
        setFiles((xs) => [...xs, { id: p.attachment.id, filename: file.name }]);
      else onDone();
      toast.success('Arquivo enviado');
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const open = (mode: 'comment' | 'task') => {
    modeRef.current = mode;
    if (!inputRef.current) {
      const el = document.createElement('input');
      el.type = 'file';
      el.onchange = () => {
        const f = el.files?.[0];
        if (f) void run(f);
        el.value = '';
      };
      inputRef.current = el;
    }
    inputRef.current.click();
  };
  return {
    files,
    readyIds: files.map((f) => f.id),
    busy,
    pick: () => open('comment'),
    pickForTask: () => open('task'),
    reset: () => setFiles([]),
  };
}

// ---------- Solicitações e formulários ----------

const REQ_TONE: Record<string, BadgeTone> = {
  NEW: 'accent',
  TRIAGE: 'warning',
  APPROVED: 'success',
  REJECTED: 'neutral',
};

export function PortalRequestsPage() {
  const { data, isLoading } = useQuery({
    queryKey: keys.requests,
    queryFn: () => portalApi.get<PortalRequest[]>('/requests'),
  });
  const rows = data ?? [];
  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-xl font-medium">Suas solicitações</h1>
          <p className="text-sm text-text-2">
            Pedidos enviados pelos formulários e o que aconteceu com cada um.
          </p>
        </div>
        <Link
          to="/portal/solicitar"
          className="ml-auto inline-flex h-control items-center gap-1.5 rounded bg-accent px-3 text-sm font-medium text-text-inverse"
        >
          <FilePlus2 className="size-4" /> Nova solicitação
        </Link>
      </header>
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="Nenhuma solicitação"
            description="Envie um pedido pelo formulário e acompanhe aqui."
          />
        ) : (
          <ul>
            {rows.map((r) => (
              <li
                key={r.id}
                className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{r.title}</p>
                  <p className="font-mono text-xs text-text-3">
                    {r.form_name} · {fmtRelative(r.created_at)}
                  </p>
                </div>
                <Badge tone={REQ_TONE[r.status] ?? 'neutral'}>
                  {REQUEST_STATUS_LABEL[r.status]}
                </Badge>
                {r.task ? (
                  <Link
                    to={`/portal/demandas/${r.task.id}`}
                    className="font-mono text-xs text-accent hover:underline"
                  >
                    #{r.task.number}
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function PortalFormsPage() {
  const { data, isLoading } = useQuery({
    queryKey: keys.forms,
    queryFn: () => portalApi.get<PortalForm[]>('/forms'),
  });
  const rows = data ?? [];
  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-xl font-medium">Nova solicitação</h1>
        <p className="text-sm text-text-2">
          Escolha o tipo de pedido. Ele entra na triagem da equipe.
        </p>
      </header>
      {isLoading ? (
        <SkeletonRows rows={2} />
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-border bg-surface-1">
          <EmptyState
            icon={FilePlus2}
            title="Nenhum formulário disponível"
            description="A equipe ainda não liberou formulários para o portal."
          />
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((f) => (
            <Link
              key={f.id}
              to={`/portal/solicitar/${f.id}`}
              className="flex flex-col gap-1 rounded-md border border-border bg-surface-1 p-4 transition-colors hover:border-accent-border"
            >
              <span className="font-medium">{f.name}</span>
              {f.description ? <span className="text-sm text-text-2">{f.description}</span> : null}
              <span className="mt-1 font-mono text-xs text-text-3">{f.fields.length} campos</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function PortalFormPage() {
  const { formId = '' } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const { data: form, isLoading } = useQuery({
    queryKey: [...keys.forms, formId],
    queryFn: () => portalApi.get<PortalForm>(`/forms/${formId}`),
  });
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [key] = useState(() => crypto.randomUUID());
  const submit = useMutation({
    mutationFn: () =>
      portalApi.post<SubmitResult>(
        `/forms/${formId}/submit`,
        { answers },
        { 'idempotency-key': key },
      ),
  });
  if (isLoading || !form) return <Skeleton className="h-60 rounded-md" />;
  const set = (n: string, v: unknown) => setAnswers((a) => ({ ...a, [n]: v }));
  return (
    <div className="mx-auto flex w-full max-w-[640px] flex-col gap-4">
      <div className="flex items-center gap-1.5 font-mono text-xs text-text-3">
        <Link to="/portal/solicitar" className="hover:text-text-1">
          Nova solicitação
        </Link>
        <ChevronRight className="size-3" />
        <span>{form.name}</span>
      </div>
      <form
        className="flex flex-col gap-4 rounded-md border border-border bg-surface-1 p-5"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          const local: Record<string, string> = {};
          for (const f of form.fields) {
            const v = answers[f.name];
            if (
              f.required &&
              (v === undefined || v === '' || v === null || (Array.isArray(v) && v.length === 0))
            )
              local[f.name] = 'Campo obrigatório.';
          }
          setIssues(local);
          if (Object.keys(local).length) return;
          submit.mutate(undefined, {
            onSuccess: () => {
              toast.success('Solicitação enviada');
              void qc.invalidateQueries({ queryKey: keys.requests });
              void navigate('/portal/solicitacoes');
            },
            onError: (err) => {
              const d = (err as { details?: { fields?: { name: string; message: string }[] } })
                .details;
              if (d?.fields)
                setIssues(Object.fromEntries(d.fields.map((i) => [i.name, i.message])));
              toast.error(err);
            },
          });
        }}
      >
        <header>
          <h1 className="text-lg font-medium">{form.name}</h1>
          {form.description ? <p className="text-sm text-text-2">{form.description}</p> : null}
        </header>
        {form.fields.map((f) => (
          <PortalFieldInput
            key={f.id}
            field={f}
            value={answers[f.name]}
            onChange={(v) => set(f.name, v)}
            error={issues[f.name]}
          />
        ))}
        <Button type="submit" variant="primary" className="self-end" loading={submit.isPending}>
          <Send /> Enviar
        </Button>
      </form>
    </div>
  );
}

function PortalFieldInput({
  field,
  value,
  onChange,
  error,
}: {
  field: PortalForm['fields'][number];
  value: unknown;
  onChange: (v: unknown) => void;
  error?: string;
}) {
  const id = `pf-${field.name}`;
  const label = `${field.label}${field.required ? ' *' : ''}`;
  const text = str(value);
  switch (field.field_type) {
    case 'TEXTAREA':
      return (
        <Field label={label} htmlFor={id} hint={field.help_text ?? undefined} error={error}>
          <Textarea
            id={id}
            value={text}
            onChange={(e) => onChange(e.target.value)}
            className="min-h-[96px]"
          />
        </Field>
      );
    case 'BOOLEAN':
      return (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={value === true} onCheckedChange={(v) => onChange(v === true)} />{' '}
          {label}
        </label>
      );
    case 'SELECT':
      return (
        <Field label={label} htmlFor={id} hint={field.help_text ?? undefined} error={error}>
          <Select id={id} value={text} onChange={(e) => onChange(e.target.value || undefined)}>
            <option value="">Selecione…</option>
            {field.options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
        </Field>
      );
    case 'MULTISELECT': {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{label}</span>
          <div className="flex flex-wrap gap-2">
            {field.options.map((o) => (
              <label
                key={o}
                className="flex items-center gap-2 rounded border border-border px-2 py-1 text-sm"
              >
                <Checkbox
                  checked={arr.includes(o)}
                  onCheckedChange={(v) => onChange(v ? [...arr, o] : arr.filter((x) => x !== o))}
                />
                {o}
              </label>
            ))}
          </div>
          {error ? <span className="text-xs text-danger">{error}</span> : null}
        </div>
      );
    }
    case 'NUMBER':
    case 'CURRENCY':
      return (
        <Field label={label} htmlFor={id} hint={field.help_text ?? undefined} error={error}>
          <Input
            id={id}
            type="number"
            step={field.field_type === 'CURRENCY' ? '0.01' : 'any'}
            mono
            value={text}
            onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
          />
        </Field>
      );
    case 'DATE':
      return (
        <Field label={label} htmlFor={id} hint={field.help_text ?? undefined} error={error}>
          <Input
            id={id}
            type="date"
            mono
            value={text}
            onChange={(e) => onChange(e.target.value || undefined)}
          />
        </Field>
      );
    case 'DATETIME':
      return (
        <Field label={label} htmlFor={id} hint={field.help_text ?? undefined} error={error}>
          <Input
            id={id}
            type="datetime-local"
            mono
            value={text}
            onChange={(e) =>
              onChange(e.target.value ? new Date(e.target.value).toISOString() : undefined)
            }
          />
        </Field>
      );
    default:
      return (
        <Field label={label} htmlFor={id} hint={field.help_text ?? undefined} error={error}>
          <Input
            id={id}
            type={
              field.field_type === 'EMAIL' ? 'email' : field.field_type === 'URL' ? 'url' : 'text'
            }
            value={text}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder ?? undefined}
          />
        </Field>
      );
  }
}
